import { colors } from '@/theme/tokens';

/**
 * `DESIGN.md` §6.2: the charts must look like this product rather than like Recharts.
 *
 * - no gridlines except a single `rule` baseline; no legend box — the series is labelled directly;
 * - no chart border, no background fill;
 * - axis labels in eyebrow type: 11px, tracked, `ink-500`;
 * - no animation on mount — "the data appearing is not an event".
 *
 * Recharts takes these as props rather than as classes, which is why they live here as one shared
 * object instead of being re-typed on every axis.
 */
export const axisTick = {
  fontSize: 11,
  letterSpacing: '0.08em',
  fill: colors.ink[500],
} as const;

export const axisLine = { stroke: colors.rule.DEFAULT } as const;

export const barLabel = { fontSize: 11, fill: colors.ink[500] } as const;

/** The one motion rule that applies to a chart: there is none (§3 Motion, §6.2). */
export const NO_ANIMATION = false;

/** Bars in `ink-700` — every bar of *Recommendations*, and every unaccented *Scores* column. */
export const barFill = colors.ink[700];

/**
 * The accent on the **modal** column of §6.2 Section 4's *Scores* chart.
 *
 * ⚠️ **This is TASK-129's deletion `reversed`, not a rule found wrong** (§6.2, 2026-08-12, TASK-148;
 * built by TASK-154, with TASK-127..129's arguments known — they were put to the user on 2026-08-12
 * and the user chose the original design). §6.2 asked for the accented modal column, that shipped,
 * and TASK-129 removed it because the accent is §3's primary-action colour and spends §1's *"colour
 * reserved for meaning"* on a fact the bar's own height already states — on the deployed screen it
 * read as a warning about the score `5`. Two things answer that objection rather than dismissing it:
 * the count is now printed **above every column**, so the accent is no longer what tells a reader
 * which bucket is largest, and the accent is the **tenant's** colour — the same fill the selected
 * period segment wears in the header two sections earlier, where it means *"this one"*.
 *
 * `hsl(var(--accent))` and not a hex: the accent is white-label (§2), so the fill has to resolve
 * through the tenant's own variable exactly as every accented control on the screen does.
 */
export const modalColumnFill = 'hsl(var(--accent))';

/**
 * §6.2's _Measured sizes_ target for the tallest *Scores* column — **≈ 83 px** — and the geometry
 * that makes the rendered column measure it (2026-08-12, TASK-158's live pass returned 56.3 px).
 *
 * A recharts column's height is `plotHeight × value / domainMax`, and the plot area is the `svg`
 * height minus the top margin minus the axis band. So the target is reached by **deriving the `svg`
 * height from it** rather than by choosing a number and measuring what falls out:
 *
 * ```
 * svg height = plot area (83) + count labels above the columns (20) + the axis beneath (30) = 133
 * ```
 *
 * ⚠️ **Height alone was not the miss, and the arithmetic says so.** At `height={140}` the plot area
 * was already 90 px — *larger* than the target — and the tallest column still measured 56.3 px,
 * because recharts' default `auto` domain rounds the maximum up to a nice tick (a modal count of 5
 * gives a domain of 8, so the column drew 5/8 of 90 = 56.25 px). The domain is pinned to `dataMax`
 * below for that reason: only then does *"the tallest column fills the plot area"* — which §6.2 has
 * always asserted — become true of any dataset instead of of a convenient one.
 *
 * The **width** is untouched at `barSize={85}`, which measured 85.0 px against a target of ≈ 85.
 */
export const SCORES_PLOT_HEIGHT = 83;
/** The `BarChart` top margin: the room the count printed above each column occupies. */
export const SCORES_LABEL_MARGIN = 20;
/**
 * The X axis band. It is recharts' own default written out, because it is a term in the height
 * above: a default that moves in a minor release would silently move the measured column.
 */
export const SCORES_AXIS_HEIGHT = 30;
export const SCORES_CHART_HEIGHT = SCORES_PLOT_HEIGHT + SCORES_LABEL_MARGIN + SCORES_AXIS_HEIGHT;

/**
 * The hidden Y axis's domain: `0` to the largest count, so the modal column reaches the top of the
 * plot area exactly.
 *
 * The axis renders nothing — there are no ticks whose roundness a reader could want — and every
 * count is printed above its own column, so the headroom recharts' `auto` domain reserves for a nice
 * tick buys no legibility and costs a third of the chart's height. `Math.max(…, 1)` covers the one
 * degenerate case that reaches this chart: a period with verdicts but no answered question at all,
 * where the section's empty state does not fire and every count is `0`; without it the domain would
 * be `[0, 0]` and the zero labels would float in the middle of the plot instead of sitting on the
 * baseline.
 */
export const scoresDomainMax = (dataMax: number) => Math.max(dataMax, 1);

/**
 * §3: the two semantic values, and nothing beyond them. A recommendation is a judgement, so it
 * gets colour; a status is mostly a state, so it stays in neutral ink except where §3 says
 * otherwise.
 */
export const verdictFill: Record<string, string> = {
  STRONG_YES: colors.positive.DEFAULT,
  YES: colors.positive.DEFAULT,
  NO: colors.negative.DEFAULT,
  STRONG_NO: colors.negative.DEFAULT,
};

/**
 * A **stated opacity** of a §3 colour, as an 8-digit hex, so no new colour enters the design system.
 *
 * §3 already uses the device (`hsl(var(--accent) / 0.08)`); what it does not have is a second green,
 * and Section 3 needs one because `OFFER` and `HIRED` shipped in the same `positive` and read as a
 * single block — a bar with five segments showing four. The opacity is computed from the token rather
 * than written out as a colour, so `src/theme/tokens.ts` stays out of this diff and the two greens
 * cannot drift apart.
 */
export const withOpacity = (hex: string, opacity: number): string =>
  `${hex}${Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase()}`;

/**
 * The opacity `HIRED` takes, **stated because §6.2 asks for the number** (TASK-153, 2026-08-12).
 *
 * ⚠️ **`positive.wash` is the tempting third value and is the wrong one here**, which §6.2 says in as
 * many words: `#EEF7F1` is a near-paper tint designed as a background *behind text*, and as a 32 px
 * segment sitting on `paper` it reads as empty track rather than as a value. `0.6` keeps the segment
 * unmistakably a value — mid green against `positive`'s dark green — and lands on `#99` exactly, which
 * is the same colour Tailwind's `bg-positive/60` gives the legend dot beside it, so the chart and its
 * key are one value in two renderings rather than two values that match by eye.
 */
export const HIRED_FILL_OPACITY = 0.6;

export const statusFill: Record<string, string> = {
  NEW: colors.rule.strong,
  INTERVIEWING: colors.ink[700],
  OFFER: colors.positive.DEFAULT,
  HIRED: withOpacity(colors.positive.DEFAULT, HIRED_FILL_OPACITY),
  REJECTED: colors.negative.DEFAULT,
};

/**
 * The legend's own dot, as a Tailwind class per status — the same five values `statusFill` gives the
 * bar, in the form a `<span>` can wear (no inline styles, `.claude/rules/frontend.md`).
 *
 * ⚠️ **The legend is one treatment five times, and `components/ui/StatusDot.tsx` deliberately does not
 * reach it** (§6.2 Section 3, 2026-08-12). What shipped used three treatments — a hollow dot for `NEW`,
 * a solid one, and a translucent pill for `HIRED` alone — because §3 renders those statuses that way
 * *beside text*. In a legend a mark is a **series key**, saying only *"this colour is that segment"*, so
 * one of five being a different shape is the shape making a claim the data does not support. `StatusDot`
 * is shared by §6.3, §6.5, §6.7 and this section's own Recommendations table and is not modified; the
 * uniform dot is local to this screen (§4: anything used on only one screen belongs in that screen's
 * folder).
 */
export const statusDotClass: Record<string, string> = {
  NEW: 'bg-rule-strong',
  INTERVIEWING: 'bg-ink-700',
  OFFER: 'bg-positive',
  HIRED: 'bg-positive/60',
  REJECTED: 'bg-negative',
};

/** Hairline separation between stacked segments, drawn in the surface colour (§6.2 Section 3). */
export const segmentStroke = colors.paper.DEFAULT;
